import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput, selectMenuStyles, SelectWrapper, DeprecatedCloseIcon } from '@arnold/common';
import { SurveyStatus } from '../../../generated/hooks';
import { CategoryIcon } from './CategoryIcons';
import {
  FilterSectionLabel,
  FilterButtonsContainer,
  SearchAndSortContainer,
  CategoryFilterButton,
} from './TopicsFiltersStyled';

export enum TopicType {
  CUSTOM = 'custom',
  SYSTEM = 'system',
}

export enum SortOption {
  GROUP_BY_CATEGORY = 'group_by_category',
  UPDATED_AT_ASC = 'updated_at_asc',
  UPDATED_AT_DESC = 'updated_at_desc',
  RUN_AT_ASC = 'run_at_asc',
  RUN_AT_DESC = 'run_at_desc',
}

export const NOT_RUN_STATUS = 'NOT_RUN';

type TopicsFiltersProps = {
  selectedTopicTypes: Set<TopicType>;
  onTopicTypeToggle: (type: TopicType) => void;
  onClearTopicTypes: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  selectedCategories: Set<string>;
  onCategoryToggle: (category: string) => void;
  onClearCategories: () => void;
  selectedSurveyStatuses: Set<string>;
  onStatusToggle: (status: string) => void;
  onClearStatuses: () => void;
  availableCategories: string[];
  categoryCodesToNames: Record<string, string>;
  variant?: 'default' | 'sticky';
  topicTypeCounts?: Record<TopicType, number>;
  statusCounts?: Record<string, number>;
  categoryCounts?: Record<string, number>;
  hideCategoriesInFilter?: boolean;
};

export const TopicsFilters: FC<TopicsFiltersProps> = ({
  selectedTopicTypes,
  onTopicTypeToggle,
  onClearTopicTypes,
  searchQuery,
  onSearchChange,
  sortOption,
  onSortChange,
  selectedCategories,
  onCategoryToggle,
  onClearCategories,
  selectedSurveyStatuses,
  onStatusToggle,
  onClearStatuses,
  availableCategories,
  categoryCodesToNames,
  variant = 'default',
  topicTypeCounts,
  statusCounts,
  categoryCounts,
  hideCategoriesInFilter,
}) => {
  const { t } = useTranslation('topics');

  const sortOptions = [
    {
      label: t('groupByCategory'),
      value: SortOption.GROUP_BY_CATEGORY,
    },
    {
      label: t('updatedAtAsc'),
      value: SortOption.UPDATED_AT_ASC,
    },
    {
      label: t('updatedAtDesc'),
      value: SortOption.UPDATED_AT_DESC,
    },
    {
      label: t('runAtAsc'),
      value: SortOption.RUN_AT_ASC,
    },
    {
      label: t('runAtDesc'),
      value: SortOption.RUN_AT_DESC,
    },
  ];

  const isSticky = variant === 'sticky';
  const topicTypeLabelMargin = isSticky ? 'mt-4 mb-3' : 'mt-6 mb-4';
  const categoryLabelMargin = isSticky ? 'mt-4 mb-3' : 'mt-6 mb-4';
  return (
    <>
      <SearchAndSortContainer className="d-flex items-center gap-2">
        <SearchInput
          dataIcom="topics-filters-search-input"
          dataCy="search-input"
          className="flex-grow-1 mr-8"
          value={searchQuery}
          placeholder={t('search')}
          onChange={(e) => {
            onSearchChange(e);
          }}
        />
        <SelectWrapper
          dataCy="sort-select-wrapper"
          value={sortOptions.find((option) => option.value === sortOption)}
          options={sortOptions}
          onChange={(option: any) => {
            onSortChange(option.value as SortOption);
          }}
          styles={{
            menu: (provided: any, state: any) => ({
              ...provided,
              ...selectMenuStyles,
            }),
          }}
          menuPlacement="auto"
        />
      </SearchAndSortContainer>

      <div className="d-flex flex-wrap justify-content-between">
        <div>
          <FilterSectionLabel className={topicTypeLabelMargin}>{t('filterByType')}:</FilterSectionLabel>
          <FilterButtonsContainer
            className="mb-3 d-flex flex-wrap gap-2 align-items-center"
            data-icom="topics-filters-topic-type-buttons"
          >
            {[TopicType.SYSTEM, TopicType.CUSTOM].map((type) => {
              const isSelected = selectedTopicTypes.has(type);
              const count = topicTypeCounts?.[type] ?? 0;
              return (
                <CategoryFilterButton
                  data-icom={`topics-filters-topic-type-button-${type}`}
                  data-cy={`filter-by-type-button-${type.toLowerCase()}`}
                  key={type}
                  $isSelected={isSelected}
                  disabled={count === 0 && !isSelected}
                  onClick={() => {
                    onTopicTypeToggle(type);
                  }}
                >
                  {type === TopicType.SYSTEM ? t('system') : t('custom')}
                  {count !== undefined && ` (${count})`}
                </CategoryFilterButton>
              );
            })}
            <CategoryFilterButton
              data-icom="topics-filters-topic-type-clear-button"
              data-cy="filter-by-type-button-clear"
              $isSelected={false}
              onClick={onClearTopicTypes}
              style={{
                opacity: selectedTopicTypes.size > 0 ? 1 : 0,
                pointerEvents: selectedTopicTypes.size > 0 ? 'auto' : 'none',
              }}
            >
              <DeprecatedCloseIcon style={{ width: '12px', height: '12px' }} />
              {t('clearFilters')}
            </CategoryFilterButton>
          </FilterButtonsContainer>
        </div>

        <div>
          <FilterSectionLabel className={topicTypeLabelMargin}>{t('filterByStatus')}:</FilterSectionLabel>
          <FilterButtonsContainer
            className="mb-3 d-flex flex-wrap gap-2 align-items-center"
            data-icom="topics-filters-survey-status-buttons"
          >
            {[SurveyStatus.Prepared, SurveyStatus.Ongoing, SurveyStatus.Completed, NOT_RUN_STATUS].map((status) => {
              const isSelected = selectedSurveyStatuses.has(status);
              const count = statusCounts?.[status] ?? 0;
              return (
                <CategoryFilterButton
                  data-icom={`topics-filters-survey-status-button-${status.toLowerCase()}`}
                  data-cy={`filter-by-status-button-${status.toLowerCase()}`}
                  key={status}
                  $isSelected={isSelected}
                  disabled={count === 0 && !isSelected}
                  onClick={() => {
                    onStatusToggle(status);
                  }}
                >
                  {status === NOT_RUN_STATUS ? t('notRun') : t(`survey:status-${status}`, { defaultValue: status })}
                  {count !== undefined && ` (${count})`}
                </CategoryFilterButton>
              );
            })}
            <CategoryFilterButton
              data-icom="topics-filters-survey-status-clear-button"
              data-cy="filter-by-status-button-clear"
              $isSelected={false}
              onClick={onClearStatuses}
              style={{
                opacity: selectedSurveyStatuses.size > 0 ? 1 : 0,
                pointerEvents: selectedSurveyStatuses.size > 0 ? 'auto' : 'none',
              }}
            >
              <DeprecatedCloseIcon style={{ width: '12px', height: '12px' }} />
              {t('clearFilters')}
            </CategoryFilterButton>
          </FilterButtonsContainer>
        </div>
      </div>

      {availableCategories.length > 0 && !hideCategoriesInFilter && (
        <>
          <FilterSectionLabel className={categoryLabelMargin}>{t('filterByCategory')}:</FilterSectionLabel>
          <FilterButtonsContainer
            className="mb-3 d-flex flex-wrap gap-2 align-items-center"
            data-icom="topics-filters-category-buttons"
          >
            {availableCategories.map((categoryCode) => {
              const isSelected = selectedCategories.has(categoryCode);
              const isUncategorized = categoryCode === 'uncategorized';
              const categoryName = isUncategorized
                ? t('uncategorized')
                : categoryCodesToNames[categoryCode] || categoryCode;
              const count = categoryCounts?.[categoryCode] ?? 0;
              return (
                <CategoryFilterButton
                  data-icom={`topics-filters-category-button-${categoryCode}`}
                  data-cy={`filter-by-category-button-${categoryCode.toLowerCase()}`}
                  key={categoryCode}
                  $isSelected={isSelected}
                  disabled={count === 0 && !isSelected}
                  onClick={() => {
                    onCategoryToggle(categoryCode);
                  }}
                >
                  {!isUncategorized && (
                    <CategoryIcon
                      categoryCode={categoryCode}
                      custom={false}
                      style={{ width: '16px', height: '16px' }}
                    />
                  )}
                  {categoryName}
                  {count !== undefined && ` (${count})`}
                </CategoryFilterButton>
              );
            })}
            <CategoryFilterButton
              data-icom="topics-filters-category-clear-button"
              data-cy="filter-by-category-button-clear"
              $isSelected={false}
              onClick={onClearCategories}
              style={{
                opacity: selectedCategories.size > 0 ? 1 : 0,
                pointerEvents: selectedCategories.size > 0 ? 'auto' : 'none',
              }}
            >
              <DeprecatedCloseIcon style={{ width: '12px', height: '12px' }} />
              {t('clearFilters')}
            </CategoryFilterButton>
          </FilterButtonsContainer>
        </>
      )}
    </>
  );
};
